/**
 * useSoundEffect — Web Audio API 音效 hook
 * 所有音效都用程式合成，不需要外部音效檔案
 */

type SoundType =
  | 'click'    // 一般點擊
  | 'confirm'  // 確認/提交
  | 'success'  // 成功/完成
  | 'unlock'   // 解鎖
  | 'warning'  // 危險操作
  | 'start'    // 開始遊戲/任務
  | 'stop'     // 結束任務
  | 'lottery'  // 抽籤
  | 'reward'   // 領取獎勵
  | 'error';   // 錯誤

let audioCtx: AudioContext | null = null;

function getAudioContext(): AudioContext | null {
  try {
    if (!audioCtx || audioCtx.state === 'closed') {
      audioCtx = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
    }
    return audioCtx;
  } catch {
    return null;
  }
}

function playTone(
  ctx: AudioContext,
  type: 'sine' | 'square' | 'sawtooth' | 'triangle',
  freq: number,
  gainVal: number,
  startTime: number,
  duration: number,
  endFreq?: number,
) {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.connect(gain);
  gain.connect(ctx.destination);

  osc.type = type;
  osc.frequency.setValueAtTime(freq, startTime);
  if (endFreq !== undefined) {
    osc.frequency.exponentialRampToValueAtTime(endFreq, startTime + duration);
  }

  gain.gain.setValueAtTime(0, startTime);
  gain.gain.linearRampToValueAtTime(gainVal, startTime + 0.01);
  gain.gain.exponentialRampToValueAtTime(0.001, startTime + duration);

  osc.start(startTime);
  osc.stop(startTime + duration + 0.01);
}

function playNoise(ctx: AudioContext, gainVal: number, startTime: number, duration: number, filterFreq: number) {
  const bufferSize = ctx.sampleRate * duration;
  const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < bufferSize; i++) {
    data[i] = Math.random() * 2 - 1;
  }
  const source = ctx.createBufferSource();
  source.buffer = buffer;

  const filter = ctx.createBiquadFilter();
  filter.type = 'bandpass';
  filter.frequency.value = filterFreq;
  filter.Q.value = 1.5;

  const gain = ctx.createGain();
  gain.gain.setValueAtTime(gainVal, startTime);
  gain.gain.exponentialRampToValueAtTime(0.001, startTime + duration);

  source.connect(filter);
  filter.connect(gain);
  gain.connect(ctx.destination);
  source.start(startTime);
  source.stop(startTime + duration);
}

const SOUND_PLAYERS: Record<SoundType, (ctx: AudioContext) => void> = {
  // 輕柔的點擊聲 — 短促高頻
  click: (ctx) => {
    const t = ctx.currentTime;
    playTone(ctx, 'sine', 880, 0.08, t, 0.06, 660);
  },

  // 確認音 — 上升雙音
  confirm: (ctx) => {
    const t = ctx.currentTime;
    playTone(ctx, 'sine', 440, 0.12, t, 0.12);
    playTone(ctx, 'sine', 660, 0.10, t + 0.1, 0.15);
  },

  // 成功音 — 三音上升
  success: (ctx) => {
    const t = ctx.currentTime;
    playTone(ctx, 'sine', 523, 0.12, t, 0.12);
    playTone(ctx, 'sine', 659, 0.12, t + 0.1, 0.12);
    playTone(ctx, 'sine', 784, 0.14, t + 0.2, 0.22);
  },

  // 解鎖音 — 神秘上升掃頻
  unlock: (ctx) => {
    const t = ctx.currentTime;
    playTone(ctx, 'sine', 300, 0.10, t, 0.35, 900);
    playTone(ctx, 'triangle', 600, 0.06, t + 0.15, 0.25, 1200);
  },

  // 警告音 — 低沉雙音
  warning: (ctx) => {
    const t = ctx.currentTime;
    playTone(ctx, 'sawtooth', 220, 0.08, t, 0.12);
    playTone(ctx, 'sawtooth', 180, 0.08, t + 0.14, 0.18);
  },

  // 開始音 — 激昂上升
  start: (ctx) => {
    const t = ctx.currentTime;
    playTone(ctx, 'square', 220, 0.06, t, 0.08);
    playTone(ctx, 'square', 330, 0.07, t + 0.07, 0.08);
    playTone(ctx, 'sine', 440, 0.10, t + 0.14, 0.10);
    playTone(ctx, 'sine', 660, 0.12, t + 0.22, 0.20);
    playTone(ctx, 'sine', 880, 0.10, t + 0.38, 0.25);
  },

  // 結束音 — 下降
  stop: (ctx) => {
    const t = ctx.currentTime;
    playTone(ctx, 'sine', 660, 0.12, t, 0.15, 330);
    playTone(ctx, 'sine', 440, 0.08, t + 0.12, 0.20, 220);
  },

  // 抽籤音 — 滾動感
  lottery: (ctx) => {
    const t = ctx.currentTime;
    for (let i = 0; i < 6; i++) {
      const freq = 400 + Math.random() * 400;
      playTone(ctx, 'triangle', freq, 0.07, t + i * 0.06, 0.05);
    }
    playTone(ctx, 'sine', 1047, 0.14, t + 0.42, 0.30);
  },

  // 領獎音 — 閃亮
  reward: (ctx) => {
    const t = ctx.currentTime;
    playTone(ctx, 'sine', 784, 0.10, t, 0.10);
    playTone(ctx, 'sine', 988, 0.10, t + 0.08, 0.10);
    playTone(ctx, 'sine', 1175, 0.12, t + 0.16, 0.10);
    playTone(ctx, 'sine', 1568, 0.14, t + 0.24, 0.30);
    playNoise(ctx, 0.04, t + 0.24, 0.25, 3000);
  },

  // 錯誤音 — 刺耳短促
  error: (ctx) => {
    const t = ctx.currentTime;
    playTone(ctx, 'sawtooth', 200, 0.10, t, 0.08);
    playTone(ctx, 'sawtooth', 150, 0.10, t + 0.10, 0.12);
  },
};

export function useSoundEffect() {
  const play = (type: SoundType) => {
    const ctx = getAudioContext();
    if (!ctx) return;

    // Resume if suspended (browser autoplay policy)
    if (ctx.state === 'suspended') {
      ctx.resume().then(() => {
        SOUND_PLAYERS[type]?.(ctx);
      });
    } else {
      SOUND_PLAYERS[type]?.(ctx);
    }
  };

  return { play };
}
